import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CreditCard, Package, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';

interface OrderInfo {
  orderId: string;
  orderName: string;
  amount: number;
  itemCount: number;
  paymentType: string;
}

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const type = searchParams.get('type'); // certificate_issue | certificate_view
  const classId = searchParams.get('classId');
  const certificateId = searchParams.get('certificateId');
  const issuedAt = searchParams.get('issuedAt');

  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  const widgetsRef = useRef<TossPaymentsWidgets | null>(null);
  const paymentMethodsRendered = useRef(false);

  // 로그인 체크
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // 주문 생성
  useEffect(() => {
    if (!type || !user) return;

    const createOrder = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch('/api/v1/payments/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            payment_type: type === 'certificate_issue' ? 'CERTIFICATE_ISSUE' : 'CERTIFICATE_VIEW',
            class_id: classId || undefined,
            certificate_id: certificateId || undefined,
            issued_at: issuedAt ? new Date(issuedAt).toISOString() : undefined,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || '주문 생성에 실패했습니다.');
        }

        const data = await response.json();
        setOrder(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    createOrder();
  }, [type, classId, certificateId, issuedAt, user]);

  // 토스 SDK 동적 로드 + 결제 위젯 렌더링
  useEffect(() => {
    if (!order || paymentMethodsRendered.current) return;

    let cancelled = false;

    const loadAndInit = async () => {
      try {
        // SDK 동적 로드
        if (!(window as any).TossPayments) {
          await new Promise<void>((resolve, reject) => {
            // 이미 로딩 중인 스크립트가 있는지 확인
            const existing = document.querySelector('script[src*="tosspayments"]');
            if (existing) {
              existing.addEventListener('load', () => resolve());
              return;
            }
            const script = document.createElement('script');
            script.src = 'https://js.tosspayments.com/v2/standard';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('토스 SDK 로드 실패'));
            document.head.appendChild(script);
          });
        }

        if (cancelled) return;

        // DOM 요소가 준비될 때까지 대기
        await new Promise(resolve => setTimeout(resolve, 200));
        if (cancelled || !document.getElementById('payment-methods')) return;

        const TossPayments = (window as any).TossPayments;
        const clientKey = (import.meta as any).env?.PUBLIC_TOSS_CLIENT_KEY || 'test_gck_docs_Ovk5rk1EwkEbP0W43n07xlzm';
        console.log('[Payment] clientKey:', clientKey);

        const tossPayments = TossPayments(clientKey);
        console.log('[Payment] TossPayments initialized');

        const customerKey = `customer_${user?.id || 'anonymous'}`;
        console.log('[Payment] customerKey:', customerKey);

        const widgets = tossPayments.widgets({ customerKey });
        console.log('[Payment] widgets created');

        console.log('[Payment] setAmount:', { currency: 'KRW', value: order.amount });
        await widgets.setAmount({ currency: 'KRW', value: order.amount });
        console.log('[Payment] setAmount done');

        if (cancelled) return;

        console.log('[Payment] rendering payment methods...');
        await widgets.renderPaymentMethods({
          selector: '#payment-methods',
        });
        console.log('[Payment] payment methods rendered');

        console.log('[Payment] rendering agreement...');
        await widgets.renderAgreement({
          selector: '#payment-agreement',
        });
        console.log('[Payment] agreement rendered');

        widgetsRef.current = widgets;
        paymentMethodsRendered.current = true;
        console.log('[Payment] all done!');
      } catch (err: any) {
        if (!cancelled) {
          console.error('Widget init error:', err);
          console.error('Error details:', { name: err.name, code: err.code, message: err.message, stack: err.stack });
          setError(`결제 위젯 초기화 실패: ${err.message || err.name || '알 수 없는 오류'}`);
        }
      }
    };

    loadAndInit();

    return () => {
      cancelled = true;
    };
  }, [order]);

  const handlePayment = async () => {
    if (!widgetsRef.current || !order) return;

    try {
      setPaying(true);
      const origin = window.location.origin;

      await widgetsRef.current.requestPayment({
        orderId: order.orderId,
        orderName: order.orderName,
        successUrl: `${origin}/payment/success`,
        failUrl: `${origin}/payment/fail`,
      });
    } catch (err: any) {
      console.error('Payment request error:', err);
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-gray-600">주문 정보를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">결제 오류</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            돌아가기
          </button>
        </div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <MainTabsNav />

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-900">결제하기</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* 주문 상품 정보 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            주문 상품 정보
          </h2>

          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">상품명</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">단가</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">수량</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">소계</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100">
                  <td className="py-4 px-4">
                    <div className="font-medium text-gray-900">
                      {type === 'certificate_issue' ? '인증서 발급 수수료' : '인증서 조회'}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {order.orderName}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center text-gray-700">
                    1,000원
                  </td>
                  <td className="py-4 px-4 text-center text-gray-700">
                    {order.itemCount}
                  </td>
                  <td className="py-4 px-4 text-right font-medium text-gray-900">
                    {order.amount.toLocaleString()}원
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 주문 요약 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            주문 요약
          </h2>

          <div className="space-y-3">
            <div className="flex justify-between text-gray-600">
              <span>상품금액</span>
              <span>{order.amount.toLocaleString()}원</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>배송비</span>
              <span>0원</span>
            </div>
            <div className="border-t border-gray-200 pt-3 flex justify-between">
              <span className="text-lg font-bold text-gray-900">총 주문금액</span>
              <span className="text-lg font-bold text-accent">
                {order.amount.toLocaleString()}원
              </span>
            </div>
          </div>
        </div>

        {/* 결제수단 (토스 위젯) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">결제수단</h2>
          <div id="payment-methods" />
        </div>

        {/* 약관 동의 (토스 위젯) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div id="payment-agreement" />
        </div>

        {/* 결제 버튼 */}
        <button
          onClick={handlePayment}
          disabled={paying}
          className="w-full py-4 bg-primary text-white text-lg font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {paying ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              결제 처리 중...
            </span>
          ) : (
            `${order.amount.toLocaleString()}원 결제하기`
          )}
        </button>

        <p className="text-center text-sm text-gray-500">
          결제 완료 후 환불 규정은{' '}
          <Link to="/legal/refund" className="text-primary underline">
            환불정책
          </Link>
          을 확인해주세요.
        </p>
      </div>
    </div>
  );
}
